/* eslint no-unused-expressions: ["error", { "allowTernary": true }] */
/* eslint-disable react/no-did-update-set-state */
import React, { PureComponent, Children } from 'react';
import PropTypes from 'prop-types';
import { debounce, reduce, slice, noop } from 'lodash';
import classnames from 'classnames';

import {
	getOffsetWidth,
	on,
	off,
	addEventListener,
	removeEventListener,
} from '@sparefoot/react-core/utils/dom';
import { Button } from 'components/core/Button';
import Chevron from 'components/svg/Chevron';

import SliderGallery from './SliderGallery';
import './Slider.scss';

const generateElements = (children) => [
	...children.slice(-2).map((child) => ({ ...child, cloned: true })),
	...children,
	...children.slice(0, 2).map((child) => ({ ...child, cloned: true })),
];

export default class Slider extends PureComponent {
	constructor(props) {
		super(props);
		this.debouncedReset = debounce(this.reset, 250);
		this.containerInstance = null;
		this.sliderInstance = null;
		this.numberClones = Math.min(props.children.length, 2);
		this.slides = [];
		this.state = {
			loading: true,
			offset: 0,
			sliderWidth: 0,
			activeElementIndex: Math.min(props.children.length, 2),
			activeIndex: 0,
			elements: generateElements(props.children),
			isDragging: false,
		};
	}

	componentDidMount() {
		on(['resize', 'load'], this.debouncedReset);
		addEventListener(this.sliderInstance, 'touchstart', this.onTouchStart);
		addEventListener(this.sliderInstance, 'touchmove', this.onTouchMove);
		addEventListener(this.sliderInstance, 'touchend', this.onTouchEnd);
	}

	componentDidUpdate(prevProps) {
		if (this.props.children !== prevProps.children) {
			this.numberClones = Math.min(this.props.children.length, 2);
			this.props.onLoad(true);
			this.setState(
				{
					loading: true,
					elements: generateElements(this.props.children),
					activeElementIndex: Math.min(this.props.children.length, 2),
					activeIndex: 0,
				},
				this.debouncedReset,
			);
		}
	}

	componentWillUnmount() {
		off(['resize', 'load'], this.debouncedReset);
		removeEventListener(
			this.sliderInstance,
			'touchstart',
			this.onTouchStart,
		);
		removeEventListener(this.sliderInstance, 'touchmove', this.onTouchMove);
		removeEventListener(this.sliderInstance, 'touchend', this.onTouchEnd);
	}

	// Checks if the slider looped past its bound and resets the index
	onTransitionEnd = () => {
		const { activeElementIndex, animating, activeIndex } = this.state;
		const { children } = this.props;
		if (
			activeElementIndex < this.numberClones ||
			activeElementIndex >= children.length + this.numberClones
		) {
			this.setState(
				{
					activeElementIndex: activeIndex + this.numberClones,
					animating: false,
				},
				this.update,
			);
		} else if (animating) {
			this.setState({ animating: false });
		}
	};

	// Prevent child animations from propagating
	onSlideTransitionEnd = (event) => {
		event.stopPropagation();
	};

	onTouchStart = ({ touches }) => {
		this.startOffset = this.state.offset;
		this.startXPos = touches[0].pageX;
	};

	onTouchMove = (event) => {
		const { touches } = event;
		this.delta = touches[0].pageX - this.startXPos;

		if (this.state.isDragging) {
			event.preventDefault();
			event.stopPropagation();
			this.setState({ offset: this.startOffset - this.delta });

			// Detect Intent
		} else if (
			Math.abs(this.delta) > 30 &&
			!this.state.isDragging &&
			!this.state.animating
		) {
			this.setState({ isDragging: true });
			// reset start X position to accomodate drag threshold
			this.startXPos = touches[0].pageX;
		}
	};

	onTouchEnd = () => {
		this.setState({ isDragging: false });
		if (Math.abs(this.delta) > 60) {
			this.delta < 0 ? this.goToNextSlide() : this.goToPrevSlide();
		} else {
			this.update(true);
		}
	};

	// calculates the widths of child slides
	static getSliderWidth(slides, slideMargin) {
		return reduce(
			slides,
			(totalWidth, slide) =>
				totalWidth + getOffsetWidth(slide) + slideMargin,
			0,
		);
	}

	getActiveIndex = (activeElementIndex) => {
		const { children } = this.props;
		if (activeElementIndex < this.numberClones) {
			return children.length - 1;
		}
		if (activeElementIndex >= children.length + this.numberClones) {
			return 0;
		}
		return activeElementIndex - this.numberClones;
	};

	getContainerRef = (instance) => {
		this.containerInstance = instance;
	};

	getSliderRef = (instance) => {
		this.sliderInstance = instance;
	};

	goToNextSlide = () => {
		const { animating } = this.state;
		if (!animating) {
			this.setState(
				({ activeElementIndex }) => ({
					activeElementIndex: activeElementIndex + 1,
					activeIndex: this.getActiveIndex(activeElementIndex + 1),
				}),
				() => this.update(true),
			);
		}
	};

	goToPrevSlide = () => {
		const { animating } = this.state;
		if (!animating) {
			this.setState(
				({ activeElementIndex }) => ({
					activeElementIndex: activeElementIndex - 1,
					activeIndex: this.getActiveIndex(activeElementIndex - 1),
				}),
				() => this.update(true),
			);
		}
	};

	goToSlide = (index) => {
		const { animating } = this.state;
		if (!animating) {
			this.setState(
				() => ({
					activeElementIndex: index + this.numberClones,
					activeIndex: index,
				}),
				() => this.update(true),
			);
		}
	};

	handlePagerSelect = (index) => () => this.goToSlide(index);

	reset = () => {
		this.slides = this.sliderInstance.querySelectorAll('.ss-slide');
		const { slideMargin } = this.props;

		// get the width of the parent container
		this.containerWidth =
			this.containerInstance && getOffsetWidth(this.containerInstance);

		// get the width of all the images in the slider
		const sliderWidth = Slider.getSliderWidth(this.slides, slideMargin);

		if (sliderWidth !== this.state.sliderWidth) {
			this.setState({ sliderWidth });
		}

		this.update();
	};

	update = (animate) => {
		const { activeElementIndex } = this.state;
		const { slideMargin, onLoad } = this.props;

		// get its width
		const activeWidth = getOffsetWidth(this.slides[activeElementIndex]);

		// center it within the parent container
		const gutter = (this.containerWidth - activeWidth) / 2;
		const previousElements = slice(this.slides, 0, activeElementIndex);

		// offset the slider
		const offset =
			Slider.getSliderWidth(previousElements, slideMargin) - gutter;

		// move the slider
		if (offset !== this.state.offset) {
			this.setState({ offset, animating: animate });
		}

		if (this.state.loading) {
			// Keep showing the Loading spinner until the width/offset
			// attribute has had time to apply to prevent shifting
			setTimeout(() => this.setState({ loading: false }, onLoad), 20);
		}
	};

	render() {
		const {
			animating,
			activeIndex,
			activeElementIndex,
			elements,
			loading,
			sliderWidth,
			offset,
			isDragging,
		} = this.state;
		const { children, className, gallery, slideMargin } = this.props;
		return (
			<div
				className={classnames('ss-slider', className, { loading })}
				ref={this.getContainerRef}
			>
				<div className="ss-slider-wrapper">
					<ul
						ref={this.getSliderRef}
						className={classnames('ss-slider-scroller', {
							animating: animating && !isDragging,
						})}
						style={{
							width: `${sliderWidth}px`,
							transform: `translate3d(${offset * -1}px, 0, 0)`,
						}}
						onTransitionEnd={this.onTransitionEnd}
					>
						{Children.toArray(
							elements.map((child, index) => (
								<li
									onTransitionEnd={this.onSlideTransitionEnd}
									className={classnames('ss-slide', {
										clone: child.cloned,
										active: index === activeElementIndex,
									})}
									style={{ marginRight: `${slideMargin}px` }}
								>
									{React.cloneElement(child, {
										onLoad: this.debouncedReset,
									})}
								</li>
							)),
						)}
					</ul>
					<div className="slider-actions">
						<Button
							type="button"
							className="ss-slider-control previous"
							onClick={this.goToPrevSlide}
						>
							<Chevron />
							<span className="visually-hidden">
								Previous Slide
							</span>
						</Button>
						<Button
							type="button"
							className="ss-slider-control next"
							onClick={this.goToNextSlide}
						>
							<Chevron />
							<span className="visually-hidden">Next Slide</span>
						</Button>
						{!gallery && (
							<ul className="ss-slider-pager">
								{Children.toArray(
									children.map((child, index) => (
										<li>
											<Button
												className={classnames(
													'ss-pager-item',
													{
														active:
															index ===
															activeIndex,
													},
												)}
												onClick={this.handlePagerSelect(
													index,
												)}
											>
												<span className="visually-hidden">
													Show Image {index}
												</span>
											</Button>
										</li>
									)),
								)}
							</ul>
						)}
					</div>
				</div>
				{!!gallery && (
					<SliderGallery
						activeIndex={activeIndex}
						onSelect={this.goToSlide}
					>
						{children}
					</SliderGallery>
				)}
			</div>
		);
	}
}

Slider.propTypes = {
	children: PropTypes.node.isRequired,
	className: PropTypes.string,
	gallery: PropTypes.bool,
	slideMargin: PropTypes.number,
	onLoad: PropTypes.func,
};

Slider.defaultProps = {
	slideMargin: 8,
	onLoad: noop,
};
